import { DatabaseType } from '../utils/terminalConfig';

export type SqlDialectKey = 'POSTGRESQL' | 'ORACLE' | 'GENERIC';

export interface SqlSyntaxTemplate {
  label: string;
  detail: string;
  type: 'keyword' | 'snippet';
  apply: string;
  requiresExternalDdl?: boolean;
}

interface SqlSyntaxTemplateOptions {
  externalDdlEnabled?: boolean;
}

const SHARED_TEMPLATES: SqlSyntaxTemplate[] = [
  {
    label: 'SELECT (Filtered)',
    detail: 'Select with WHERE filter and limit',
    type: 'snippet',
    apply: 'SELECT ${column_list}\nFROM ${table_name}\nWHERE ${condition}\nLIMIT ${limit};',
  },
  {
    label: 'SELECT JOIN',
    detail: 'Join two tables with explicit condition',
    type: 'snippet',
    apply: 'SELECT ${a}.*, ${b}.*\nFROM ${table_a} ${a}\nJOIN ${table_b} ${b} ON ${a}.${join_col} = ${b}.${join_col}\nWHERE ${condition};',
  },
  {
    label: 'INSERT INTO',
    detail: 'Insert one row',
    type: 'snippet',
    apply: 'INSERT INTO ${table_name} (${column1}, ${column2})\nVALUES (${value1}, ${value2});',
  },
  {
    label: 'UPDATE (Filtered)',
    detail: 'Update rows using a filter',
    type: 'snippet',
    apply: 'UPDATE ${table_name}\nSET ${column_name} = ${new_value}\nWHERE ${condition};',
  },
  {
    label: 'DELETE (Filtered)',
    detail: 'Delete rows using a filter',
    type: 'snippet',
    apply: 'DELETE FROM ${table_name}\nWHERE ${condition};',
  },
  {
    label: 'CREATE TABLE',
    detail: 'Basic table definition',
    type: 'snippet',
    apply: 'CREATE TABLE ${table_name} (\n  ${column_name} ${data_type}\n);',
    requiresExternalDdl: true,
  },
  {
    label: 'ALTER TABLE ADD COLUMN',
    detail: 'Add column to existing table',
    type: 'snippet',
    apply: 'ALTER TABLE ${table_name}\nADD COLUMN ${column_name} ${data_type};',
    requiresExternalDdl: true,
  },
  {
    label: 'DROP TABLE',
    detail: 'Drop table from schema',
    type: 'snippet',
    apply: 'DROP TABLE ${table_name};',
    requiresExternalDdl: true,
  },
  {
    label: 'TRUNCATE TABLE',
    detail: 'Remove all rows from table',
    type: 'snippet',
    apply: 'TRUNCATE TABLE ${table_name};',
    requiresExternalDdl: true,
  },
  {
    label: 'CREATE VIEW',
    detail: 'Create a reusable SQL view',
    type: 'snippet',
    apply: 'CREATE VIEW ${view_name} AS\nSELECT ${column_list}\nFROM ${table_name}\nWHERE ${condition};',
    requiresExternalDdl: true,
  },
  {
    label: 'DROP VIEW',
    detail: 'Drop an existing view',
    type: 'snippet',
    apply: 'DROP VIEW ${view_name};',
    requiresExternalDdl: true,
  },
  {
    label: 'CREATE INDEX',
    detail: 'Create index on one or more columns',
    type: 'snippet',
    apply: 'CREATE INDEX ${index_name} ON ${table_name} (${column_name});',
    requiresExternalDdl: true,
  },
  {
    label: 'GRANT SELECT',
    detail: 'Grant read access on a table',
    type: 'snippet',
    apply: 'GRANT SELECT ON ${table_name} TO ${role_or_user};',
    requiresExternalDdl: true,
  },
  {
    label: 'GRANT DML',
    detail: 'Grant DML privileges on a table',
    type: 'snippet',
    apply: 'GRANT SELECT, INSERT, UPDATE, DELETE ON ${table_name} TO ${role_or_user};',
    requiresExternalDdl: true,
  },
  {
    label: 'REVOKE PRIVILEGES',
    detail: 'Revoke previously granted privileges',
    type: 'snippet',
    apply: 'REVOKE ${privilege_list} ON ${table_name} FROM ${role_or_user};',
    requiresExternalDdl: true,
  },
];

const DIALECT_TEMPLATES: Record<SqlDialectKey, SqlSyntaxTemplate[]> = {
  POSTGRESQL: [
    ...SHARED_TEMPLATES,
    {
      label: 'CREATE TABLE (PostgreSQL serial PK)',
      detail: 'Table with auto-increment primary key',
      type: 'snippet',
      apply: 'CREATE TABLE ${table_name} (\n  id BIGSERIAL PRIMARY KEY,\n  ${column_name} TEXT NOT NULL\n);',
      requiresExternalDdl: true,
    },
    {
      label: 'ALTER TABLE ALTER COLUMN TYPE',
      detail: 'PostgreSQL type migration',
      type: 'snippet',
      apply: 'ALTER TABLE ${table_name}\nALTER COLUMN ${column_name} TYPE ${new_type};',
      requiresExternalDdl: true,
    },
  ],
  ORACLE: [
    ...SHARED_TEMPLATES,
    {
      label: 'CREATE TABLE (Oracle identity PK)',
      detail: 'Table with identity primary key',
      type: 'snippet',
      apply: 'CREATE TABLE ${table_name} (\n  id NUMBER GENERATED BY DEFAULT AS IDENTITY PRIMARY KEY,\n  ${column_name} VARCHAR2(255) NOT NULL\n);',
      requiresExternalDdl: true,
    },
    {
      label: 'ALTER TABLE MODIFY COLUMN',
      detail: 'Oracle column definition change',
      type: 'snippet',
      apply: 'ALTER TABLE ${table_name}\nMODIFY (${column_name} ${new_type});',
      requiresExternalDdl: true,
    },
  ],
  GENERIC: SHARED_TEMPLATES,
};

export function resolveSqlDialect(databaseType: DatabaseType): SqlDialectKey {
  if (databaseType === 'POSTGRESQL') {
    return 'POSTGRESQL';
  }
  if (databaseType === 'ORACLE') {
    return 'ORACLE';
  }
  return 'GENERIC';
}

export function getSqlSyntaxTemplates(databaseType: DatabaseType, options: SqlSyntaxTemplateOptions = {}): SqlSyntaxTemplate[] {
  const templates = DIALECT_TEMPLATES[resolveSqlDialect(databaseType)] || SHARED_TEMPLATES;
  const externalDdlEnabled = options.externalDdlEnabled ?? false;

  if (databaseType === 'EXTERNAL' && !externalDdlEnabled) {
    return templates.filter((template) => !template.requiresExternalDdl);
  }

  return templates;
}
